import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone, Monitor, Share } from 'lucide-react';
import '../styles/recovery-design-system.css';

const InstallPrompt = ({ showOnLoad = false }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event (Chrome, Edge)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listen for custom event to open prompt
    const handleOpenPrompt = () => {
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('open-install-prompt', handleOpenPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('open-install-prompt', handleOpenPrompt);
    };
  }, [showOnLoad]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }
    
    setDeferredPrompt(null);
    setIsInstallable(false);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
    // Clear after 7 days
    setTimeout(() => {
      localStorage.removeItem('pwa-install-dismissed');
    }, 7 * 24 * 60 * 60 * 1000);
  };

  // Don't show if already installed
  if (isStandalone) {
    return null;
  }

  return (
    <>
      {/* Floating buttons removed - using dedicated section on landing page instead */}

      {/* Install Prompt Modal */}
      <AnimatePresence>
        {showPrompt && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleDismiss}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9998,
                backdropFilter: 'blur(4px)'
              }}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              style={{
                position: 'fixed',
                bottom: '1rem',
                left: '1rem',
                right: '1rem',
                margin: '0 auto',
                maxWidth: '500px',
                background: 'white',
                borderRadius: '16px',
                padding: '1.25rem',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                zIndex: 9999
              }}
              className="sm:left-1/2 sm:-translate-x-1/2 sm:right-auto sm:bottom-8 sm:p-6"
            >
              {/* Close Button */}
              <button
                onClick={handleDismiss}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  padding: '0.25rem'
                }}
              >
                <X className="w-5 h-5" />
              </button>

              {/* Icon and Title */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <img 
                  src="/icon-192.png" 
                  alt="App Icon"
                  style={{ width: '56px', height: '56px', borderRadius: '12px' }}
                  className="sm:w-16 sm:h-16"
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ 
                    fontFamily: 'var(--font-heading)',
                    color: 'var(--navy)',
                    fontSize: 'clamp(1rem, 3vw, 1.25rem)',
                    marginBottom: '0.25rem',
                    fontWeight: '600',
                    lineHeight: '1.2'
                  }}>
                    Install App
                  </h3>
                  <p style={{ 
                    color: 'var(--text-muted)',
                    fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                    margin: 0,
                    lineHeight: '1.3'
                  }}>
                    Quick access to devotionals
                  </p>
                </div>
              </div>

              {/* Benefits */}
              <div style={{ 
                background: 'var(--bg-secondary)',
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <Smartphone className="w-5 h-5" style={{ color: 'var(--sage)' }} />
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    Works offline - access devotionals anytime
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <Monitor className="w-5 h-5" style={{ color: 'var(--sage)' }} />
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    Full-screen app experience
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Download className="w-5 h-5" style={{ color: 'var(--sage)' }} />
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    No app store required - installs in seconds
                  </span>
                </div>
              </div>

              {/* Install Instructions */}
              {isIOS ? (
                // iOS Instructions
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ 
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                    marginBottom: '0.75rem',
                    fontWeight: '600'
                  }}>
                    To install on iPhone/iPad:
                  </p>
                  <ol style={{ 
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                    paddingLeft: '1.25rem',
                    margin: 0,
                    lineHeight: '1.6'
                  }}>
                    <li>Tap the <Share className="inline w-4 h-4" /> Share button</li>
                    <li>Scroll down and tap "Add to Home Screen"</li>
                    <li>Tap "Add" in the top right corner</li>
                  </ol>
                </div>
              ) : (
                // Android/Desktop Install Button
                <button
                  onClick={handleInstallClick}
                  disabled={!deferredPrompt}
                  className="btn-primary"
                  style={{
                    width: '100%',
                    fontSize: '1rem',
                    padding: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.75rem'
                  }}
                >
                  <Download className="w-5 h-5" />
                  Install App
                </button>
              )}

              {/* Dismiss Text */}
              <p style={{ 
                fontSize: '0.75rem',
                color: 'var(--text-muted)',
                textAlign: 'center',
                marginTop: '0.75rem',
                marginBottom: 0
              }}>
                You can always install later from the menu
              </p>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default InstallPrompt;
